/**
 * Jurisdiction Requestor Dashboard
 * ================================
 * Session 24 (Feb 22, 2026) — premium lightweight landing page for the
 * `jurisdiction_requestor` role only.
 *
 * Strictly additive. DOES NOT replicate the JM dashboard (no coverage map,
 * health widgets, governance tools). Focus: personal request visibility +
 * simple read-only jurisdiction count.
 *
 * All metrics are derived client-side from two existing endpoints:
 *   GET /api/jurisdictions              → global directory count (read-only)
 *   GET /api/jurisdiction-requests      → server auto-scopes to this user
 * No new backend endpoints were introduced.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { formatInTimeZone } from 'date-fns-tz';
import {
  Building2,
  Send,
  Clock,
  CheckCircle2,
  XCircle,
  RotateCcw,
  ArrowRight,
  RefreshCw,
  Info,
  TrendingUp,
  Plus,
  ListChecks,
  ExternalLink,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { getUserFromToken } from '../utils/adminApi';
import JurisdictionWizardModal from '../components/jurisdiction/JurisdictionWizardModal';

const API = process.env.REACT_APP_BACKEND_URL;
const ET_TIMEZONE = 'America/New_York';

// ─────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────

const formatET = (iso) => {
  if (!iso) return '—';
  try {
    return formatInTimeZone(new Date(iso), ET_TIMEZONE, "MMM d, yyyy");
  } catch {
    return iso;
  }
};

const humanDuration = (hours) => {
  if (hours == null || !isFinite(hours)) return '—';
  if (hours < 1) return `${Math.round(hours * 60)} min`;
  if (hours < 24) return `${hours.toFixed(1)} hrs`;
  const days = hours / 24;
  if (days < 7) return `${days.toFixed(1)} days`;
  return `${(days / 7).toFixed(1)} wks`;
};

const STATUS_META = {
  pending: {
    label: 'Pending',
    chip: 'bg-amber-50 text-amber-700 border-amber-200',
    bar: 'bg-amber-500',
    icon: Clock,
  },
  approved: {
    label: 'Approved',
    chip: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    bar: 'bg-emerald-500',
    icon: CheckCircle2,
  },
  rejected: {
    label: 'Rejected',
    chip: 'bg-rose-50 text-rose-700 border-rose-200',
    bar: 'bg-rose-500',
    icon: XCircle,
  },
  revised: {
    label: 'Revised',
    chip: 'bg-indigo-50 text-indigo-700 border-indigo-200',
    bar: 'bg-indigo-500',
    icon: RotateCcw,
  },
};

// ─────────────────────────────────────────────────────────────────────────
// Sub-components
// ─────────────────────────────────────────────────────────────────────────

/** KPI Card — Stripe-style: white, rounded-2xl, soft border, subtle shadow. */
const KpiCard = ({ label, value, helper, Icon, iconColor, testid }) => (
  <div
    className="rounded-2xl bg-white border border-slate-200 shadow-sm p-5 hover:shadow-md transition-shadow"
    data-testid={testid}
  >
    <div className="flex items-start justify-between mb-3">
      <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">{label}</p>
      <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${iconColor}`}>
        <Icon className="w-4 h-4" />
      </div>
    </div>
    <div className="text-3xl font-semibold text-slate-900 tabular-nums leading-none">{value}</div>
    {helper && <p className="text-xs text-slate-500 mt-2">{helper}</p>}
  </div>
);

/** Horizontal status bar — one row per status with count + percent. */
const StatusBar = ({ label, count, total, barClass, chipClass }) => {
  const pct = total > 0 ? Math.round((count / total) * 100) : 0;
  return (
    <div>
      <div className="flex items-center justify-between mb-1.5">
        <span className={`inline-flex px-2 py-0.5 rounded-full text-[11px] border ${chipClass}`}>
          {label}
        </span>
        <span className="text-xs text-slate-500 tabular-nums">
          {count} <span className="text-slate-400">· {pct}%</span>
        </span>
      </div>
      <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden">
        <div
          className={`h-full ${barClass} transition-all duration-500`}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
};

/** Row in Recent Requests table — includes per-status action. */
const RequestRow = ({ req, onOpenDirectory, onResubmit }) => {
  const M = STATUS_META[req.status] || STATUS_META.pending;
  const MIcon = M.icon;
  const p = req.payload || {};
  return (
    <tr className="hover:bg-slate-50 transition-colors" data-testid={`recent-req-${req.id}`}>
      <td className="px-4 py-3 font-medium text-slate-800">{p.jurisdiction_name || '—'}</td>
      <td className="px-4 py-3 text-slate-600 capitalize">{p.jurisdiction_type || '—'}</td>
      <td className="px-4 py-3 text-slate-600">{p.state || '—'}</td>
      <td className="px-4 py-3">
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] border ${M.chip}`}>
          <MIcon className="w-3 h-3" />
          {M.label}
        </span>
      </td>
      <td className="px-4 py-3 text-xs text-slate-500">{formatET(req.submitted_at)}</td>
      <td className="px-4 py-3 text-xs text-slate-500">{formatET(req.reviewed_at)}</td>
      <td className="px-4 py-3 text-xs text-slate-500 max-w-[220px] truncate" title={req.rejection_reason || ''}>
        {req.status === 'rejected' ? (req.rejection_reason || '—') : (req.submission_note || '—')}
      </td>
      <td className="px-4 py-3 text-right">
        {req.status === 'rejected' && (
          <Button
            size="sm"
            variant="outline"
            onClick={() => onResubmit(req)}
            data-testid={`row-resubmit-${req.id}`}
          >
            <RotateCcw className="w-3.5 h-3.5 mr-1" />
            Revise
          </Button>
        )}
        {req.status === 'approved' && req.approved_jurisdiction_id && (
          <Button
            size="sm"
            variant="ghost"
            onClick={() => onOpenDirectory()}
            data-testid={`row-view-${req.id}`}
          >
            View
            <ExternalLink className="w-3.5 h-3.5 ml-1" />
          </Button>
        )}
        {req.status === 'pending' && (
          <span className="text-[11px] text-slate-400 italic">Awaiting review</span>
        )}
      </td>
    </tr>
  );
};

// ─────────────────────────────────────────────────────────────────────────
// Main component
// ─────────────────────────────────────────────────────────────────────────

const JurisdictionRequestorDashboard = () => {
  const navigate = useNavigate();
  const currentUser = getUserFromToken();

  const [loading, setLoading] = useState(true);
  const [requests, setRequests] = useState([]);
  const [totalJurisdictions, setTotalJurisdictions] = useState(null);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [resubmitTarget, setResubmitTarget] = useState(null);

  // Belt & suspenders: if a non-requestor somehow reaches this URL, bounce.
  useEffect(() => {
    if (!currentUser) {
      navigate('/login', { replace: true });
      return;
    }
    if (currentUser.role !== 'jurisdiction_requestor') {
      navigate('/', { replace: true });
    }
  }, [currentUser, navigate]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    const token = localStorage.getItem('token');
    const auth = { headers: { Authorization: `Bearer ${token}` } };
    try {
      const [reqRes, jurRes] = await Promise.all([
        // Requestor scope is applied server-side; grab up to 500 to power
        // all client-side aggregates without pagination in v1.
        axios.get(`${API}/api/jurisdiction-requests`, { params: { limit: 500 }, ...auth }),
        // Lightweight: requestor has read access. We just need the array
        // length for the global count KPI.
        axios.get(`${API}/api/jurisdictions`, auth),
      ]);
      setRequests(reqRes.data?.items || []);
      const payload = jurRes.data;
      const count = Array.isArray(payload)
        ? payload.length
        : Array.isArray(payload?.items)
        ? payload.items.length
        : payload?.total ?? 0;
      setTotalJurisdictions(count);
    } catch (e) {
      console.error('[RequestorDashboard] fetch failed', e);
      toast.error(e?.response?.data?.detail || 'Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // ── Derived metrics (client-side) ─────────────────────────────────────
  const metrics = useMemo(() => {
    const all = requests;
    const pending = all.filter((r) => r.status === 'pending');
    const approved = all.filter((r) => r.status === 'approved');
    const rejected = all.filter((r) => r.status === 'rejected');
    const revised = all.filter((r) => !!r.revision_of_id);

    const reviewed = [...approved, ...rejected];
    const reviewHoursList = reviewed
      .map((r) => {
        try {
          const sub = new Date(r.submitted_at).getTime();
          const rev = new Date(r.reviewed_at).getTime();
          if (!sub || !rev || rev < sub) return null;
          return (rev - sub) / (1000 * 60 * 60);
        } catch {
          return null;
        }
      })
      .filter((v) => v != null && isFinite(v));
    const avgReviewHours =
      reviewHoursList.length > 0
        ? reviewHoursList.reduce((a, b) => a + b, 0) / reviewHoursList.length
        : null;

    const totalReviewed = approved.length + rejected.length;
    const approvalRate =
      totalReviewed > 0 ? Math.round((approved.length / totalReviewed) * 100) : null;

    const sortedBySubmittedDesc = [...all].sort(
      (a, b) => new Date(b.submitted_at) - new Date(a.submitted_at)
    );
    const sortedApprovedDesc = [...approved].sort(
      (a, b) => new Date(b.reviewed_at || 0) - new Date(a.reviewed_at || 0)
    );

    return {
      total: all.length,
      pending: pending.length,
      approved: approved.length,
      rejected: rejected.length,
      revised: revised.length,
      avgReviewHours,
      approvalRate,
      lastSubmitted: sortedBySubmittedDesc[0]?.submitted_at || null,
      lastApproved: sortedApprovedDesc[0]?.reviewed_at || null,
      recent: sortedBySubmittedDesc.slice(0, 8),
    };
  }, [requests]);

  const isEmpty = !loading && metrics.total === 0;

  // ── Resubmit handler (mirrors queue page) ─────────────────────────────
  const submitResubmit = async (newPayload) => {
    if (!resubmitTarget) return;
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/api/jurisdiction-requests/${resubmitTarget.id}/resubmit`,
        { payload: newPayload?.request?.payload || newPayload },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Revised request submitted');
    } catch {
      /* If the wizard already posted as a new request, we swallow; worst-
         case the user has both a rejected + a fresh pending row. */
    }
    setResubmitTarget(null);
    await fetchData();
  };

  // ─────────────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-slate-50 px-6 py-6" data-testid="requestor-dashboard">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900 tracking-tight">
            Jurisdiction Request Dashboard
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Track your submitted jurisdiction requests and approval status.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={fetchData}
            disabled={loading}
            data-testid="refresh-dashboard-btn"
          >
            <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button
            variant="outline"
            onClick={() => navigate('/admin/jurisdiction-requests')}
            data-testid="view-my-requests-btn"
          >
            <ListChecks className="w-4 h-4 mr-1" />
            View My Requests
          </Button>
          <Button
            onClick={() => setWizardOpen(true)}
            className="bg-indigo-600 hover:bg-indigo-700 text-white"
            data-testid="submit-new-request-btn"
          >
            <Plus className="w-4 h-4 mr-1" />
            Submit New Request
          </Button>
        </div>
      </div>

      {/* ── Empty State ─────────────────────────────────────────────── */}
      {isEmpty ? (
        <div
          className="rounded-2xl bg-white border border-slate-200 shadow-sm p-12 text-center max-w-2xl mx-auto"
          data-testid="empty-state"
        >
          <div className="w-14 h-14 mx-auto rounded-2xl bg-indigo-50 text-indigo-600 flex items-center justify-center mb-4">
            <Send className="w-7 h-7" />
          </div>
          <h3 className="text-lg font-semibold text-slate-800">
            You haven’t submitted any jurisdiction requests yet.
          </h3>
          <p className="text-sm text-slate-500 mt-2 mb-6">
            Start by proposing a new jurisdiction — a reviewer will approve or send it back with
            feedback.
          </p>
          <div className="flex items-center justify-center gap-2">
            <Button
              onClick={() => setWizardOpen(true)}
              className="bg-indigo-600 hover:bg-indigo-700 text-white"
              data-testid="empty-submit-btn"
            >
              <Plus className="w-4 h-4 mr-1" />
              Submit New Request
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate('/admin/jurisdictions')}
              data-testid="empty-browse-btn"
            >
              Browse Directory
            </Button>
          </div>
        </div>
      ) : (
        <>
          {/* ── Section 1: KPI cards ─────────────────────────────── */}
          <div
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-6"
            data-testid="kpi-row"
          >
            <KpiCard
              label="Total Jurisdictions"
              value={totalJurisdictions ?? '—'}
              helper="Global directory"
              Icon={Building2}
              iconColor="bg-slate-100 text-slate-700"
              testid="kpi-total-jurisdictions"
            />
            <KpiCard
              label="Submitted"
              value={metrics.total}
              helper="By you, all-time"
              Icon={Send}
              iconColor="bg-indigo-50 text-indigo-600"
              testid="kpi-submitted"
            />
            <KpiCard
              label="Pending Approval"
              value={metrics.pending}
              helper="Awaiting reviewer"
              Icon={Clock}
              iconColor="bg-amber-50 text-amber-600"
              testid="kpi-pending"
            />
            <KpiCard
              label="Approved"
              value={metrics.approved}
              helper="Created in directory"
              Icon={CheckCircle2}
              iconColor="bg-emerald-50 text-emerald-600"
              testid="kpi-approved"
            />
            <KpiCard
              label="Rejected / Needs Revision"
              value={metrics.rejected}
              helper={metrics.rejected > 0 ? 'Revise & resubmit' : 'None so far'}
              Icon={XCircle}
              iconColor="bg-rose-50 text-rose-600"
              testid="kpi-rejected"
            />
          </div>

          {/* ── Section 2: Status Breakdown + Approval Performance ─ */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
            {/* Status Breakdown */}
            <div
              className="rounded-2xl bg-white border border-slate-200 shadow-sm p-5"
              data-testid="status-breakdown-card"
            >
              <h3 className="text-sm font-semibold text-slate-800 mb-1">
                Request Status Breakdown
              </h3>
              <p className="text-xs text-slate-500 mb-5">Distribution of all your submissions.</p>
              <div className="space-y-4">
                <StatusBar
                  label="Pending"
                  count={metrics.pending}
                  total={metrics.total}
                  barClass={STATUS_META.pending.bar}
                  chipClass={STATUS_META.pending.chip}
                />
                <StatusBar
                  label="Approved"
                  count={metrics.approved}
                  total={metrics.total}
                  barClass={STATUS_META.approved.bar}
                  chipClass={STATUS_META.approved.chip}
                />
                <StatusBar
                  label="Rejected"
                  count={metrics.rejected}
                  total={metrics.total}
                  barClass={STATUS_META.rejected.bar}
                  chipClass={STATUS_META.rejected.chip}
                />
                {metrics.revised > 0 && (
                  <StatusBar
                    label="Revised"
                    count={metrics.revised}
                    total={metrics.total}
                    barClass={STATUS_META.revised.bar}
                    chipClass={STATUS_META.revised.chip}
                  />
                )}
              </div>
            </div>

            {/* Approval Performance */}
            <div
              className="rounded-2xl bg-white border border-slate-200 shadow-sm p-5"
              data-testid="approval-performance-card"
            >
              <div className="flex items-center justify-between mb-1">
                <h3 className="text-sm font-semibold text-slate-800">Approval Performance</h3>
                <TrendingUp className="w-4 h-4 text-slate-400" />
              </div>
              <p className="text-xs text-slate-500 mb-5">
                Calculated from your reviewed requests.
              </p>
              <dl className="grid grid-cols-2 gap-4">
                <div>
                  <dt className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">
                    Approval rate
                  </dt>
                  <dd
                    className="text-2xl font-semibold text-slate-900 tabular-nums mt-1"
                    data-testid="approval-rate"
                  >
                    {metrics.approvalRate == null ? '—' : `${metrics.approvalRate}%`}
                  </dd>
                  <p className="text-[11px] text-slate-400 mt-0.5">
                    {metrics.approved + metrics.rejected} reviewed
                  </p>
                </div>
                <div>
                  <dt className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">
                    Avg. review time
                  </dt>
                  <dd
                    className="text-2xl font-semibold text-slate-900 tabular-nums mt-1"
                    data-testid="avg-review-time"
                  >
                    {humanDuration(metrics.avgReviewHours)}
                  </dd>
                  <p className="text-[11px] text-slate-400 mt-0.5">
                    Submission → decision
                  </p>
                </div>
                <div>
                  <dt className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">
                    Last submitted
                  </dt>
                  <dd className="text-sm font-medium text-slate-800 mt-1">
                    {formatET(metrics.lastSubmitted)}
                  </dd>
                </div>
                <div>
                  <dt className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">
                    Last approved
                  </dt>
                  <dd className="text-sm font-medium text-slate-800 mt-1">
                    {formatET(metrics.lastApproved)}
                  </dd>
                </div>
              </dl>
            </div>
          </div>

          {/* ── Section 3: Recent Requests ─────────────────────────── */}
          <div
            className="rounded-2xl bg-white border border-slate-200 shadow-sm overflow-hidden mb-6"
            data-testid="recent-requests-card"
          >
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
              <div>
                <h3 className="text-sm font-semibold text-slate-800">Recent Requests</h3>
                <p className="text-xs text-slate-500 mt-0.5">
                  Your {metrics.recent.length} most recent submissions.
                </p>
              </div>
              <button
                type="button"
                onClick={() => navigate('/admin/jurisdiction-requests')}
                className="text-xs font-medium text-indigo-600 hover:text-indigo-700 flex items-center"
                data-testid="view-all-requests-link"
              >
                View All Requests
                <ArrowRight className="w-3 h-3 ml-1" />
              </button>
            </div>
            <table className="w-full text-sm">
              <thead className="tf-thead uppercase text-[11px] tracking-wider">
                <tr>
                  <th className="px-4 py-3 text-left">Jurisdiction</th>
                  <th className="px-4 py-3 text-left">Type</th>
                  <th className="px-4 py-3 text-left">State</th>
                  <th className="px-4 py-3 text-left">Status</th>
                  <th className="px-4 py-3 text-left">Submitted</th>
                  <th className="px-4 py-3 text-left">Reviewed</th>
                  <th className="px-4 py-3 text-left">Notes</th>
                  <th className="px-4 py-3 text-right">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {metrics.recent.map((r) => (
                  <RequestRow
                    key={r.id}
                    req={r}
                    onOpenDirectory={() => navigate('/admin/jurisdictions')}
                    onResubmit={(target) => setResubmitTarget(target)}
                  />
                ))}
              </tbody>
            </table>
          </div>

          {/* ── Section 4: Guidance Card ───────────────────────────── */}
          <div
            className="rounded-2xl bg-gradient-to-br from-indigo-50 to-white border border-indigo-100 p-5"
            data-testid="guidance-card"
          >
            <div className="flex items-start gap-3">
              <div className="w-9 h-9 rounded-lg bg-indigo-100 text-indigo-600 flex items-center justify-center flex-shrink-0">
                <Info className="w-4 h-4" />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-slate-800">
                  Before submitting a new jurisdiction
                </h3>
                <ul className="mt-2 space-y-1.5 text-sm text-slate-600 list-disc list-inside">
                  <li>Confirm state and jurisdiction type.</li>
                  <li>Check for duplicates in the Directory.</li>
                  <li>Enter complete collector contact details.</li>
                  <li>Use notes for any context Admin should review.</li>
                </ul>
              </div>
            </div>
          </div>
        </>
      )}

      {/* ── New-Request Wizard ─────────────────────────────────────── */}
      <JurisdictionWizardModal
        isOpen={wizardOpen}
        mode="request"
        jurisdiction={null}
        onClose={() => setWizardOpen(false)}
        onSave={async () => {
          setWizardOpen(false);
          await fetchData();
          navigate('/admin/jurisdiction-requests');
        }}
      />

      {/* ── Revise & Resubmit wizard (reuses request mode) ─────────── */}
      {resubmitTarget && (
        <JurisdictionWizardModal
          isOpen
          mode="request"
          jurisdiction={{ ...resubmitTarget.payload, _resubmitOf: resubmitTarget.id }}
          onClose={() => setResubmitTarget(null)}
          onSave={submitResubmit}
        />
      )}
    </div>
  );
};

export default JurisdictionRequestorDashboard;
